/**
 * Host-authoritative session glue.
 *
 * The host owns the only real sim. Guests send inputs and render the snapshots
 * they get back -- one full snapshot per broadcast, no delta encoding.
 *
 * Slots come from the lobby roster rather than from connection order, so the
 * blob a player drives is the one they picked a side for.
 *
 * Guests predict their own blob (see predictor.ts); everything else on their
 * screen comes from these snapshots.
 */
import { type BlobInput, NO_INPUT, type SimState, type VolleySim } from '@office/sim';
import { type InputMessage, type NetMessage, SequenceGate, encode } from './protocol.js';
import { SnapshotBuffer, type SnapshotView } from './snapshot-buffer.js';
import type { NetGuest, NetHost } from './transport.js';

/** 120 Hz sim / 4 = 30 Hz snapshots. */
export const SNAPSHOT_EVERY_TICKS = 4;
/**
 * Guest inputs go out every second sim tick, which is the 60 Hz the spec asks
 * for. The caller does that pacing on the sim clock rather than a wall clock,
 * so an input is always tagged with the tick it was predicted on.
 */
export const INPUT_EVERY_TICKS = 2;
const PING_INTERVAL_MS = 1000;

export class HostSession {
  private seq = 0;
  /** slot -> peer id. Slot 0 is the local player, so it is always null. */
  private slotPeers: (string | null)[];
  private readonly inputs: BlobInput[];
  private readonly gates = new Map<string, SequenceGate>();

  localInput: BlobInput = NO_INPUT;

  constructor(
    readonly sim: VolleySim,
    readonly net: NetHost,
  ) {
    this.slotPeers = sim.blobs.map(() => null);
    this.inputs = sim.blobs.map(() => NO_INPUT);
  }

  /**
   * Bind slots to peers. `peerIds[i]` drives blob i; null means the local
   * player (slot 0) or a slot nobody filled.
   */
  setRoster(peerIds: readonly (string | null)[]): void {
    this.slotPeers = this.sim.blobs.map((_, i) => peerIds[i] ?? null);
    this.gates.clear();
    for (const id of this.slotPeers) {
      if (id) this.gates.set(id, new SequenceGate());
    }
    this.inputs.fill(NO_INPUT);
  }

  dropPeer(peerId: string): void {
    const slot = this.slotPeers.indexOf(peerId);
    if (slot >= 0) {
      this.slotPeers[slot] = null;
      this.inputs[slot] = NO_INPUT;
    }
    this.gates.delete(peerId);
  }

  /** Returns true if the message belonged to the match rather than the lobby. */
  handleMessage(peerId: string, message: NetMessage): boolean {
    if (message.t === 'ping') {
      this.net.send(peerId, encode({ t: 'pong', sent: message.sent }));
      return true;
    }
    if (message.t !== 'input') return false;
    const slot = this.slotPeers.indexOf(peerId);
    if (slot < 0) return true;
    // Unordered channel: a late input is stale by definition, so drop it.
    if (!this.gates.get(peerId)?.accept((message as InputMessage).seq)) return true;
    this.inputs[slot] = message.input;
    return true;
  }

  /** The inputs applied on the last step, by slot. Read-only, for the renderer. */
  currentInputs(): readonly BlobInput[] {
    return this.inputs;
  }

  /** One fixed step, plus a snapshot broadcast on the 30 Hz beat. */
  step(): void {
    this.inputs[0] = this.localInput;
    this.sim.step(this.inputs);
    if (this.sim.tick % SNAPSHOT_EVERY_TICKS === 0) this.broadcast();
  }

  private broadcast(): void {
    if (this.net.openCount === 0) return;
    const message: NetMessage = {
      t: 'snap',
      seq: this.seq++,
      sent: Date.now(),
      state: this.sim.snapshot(),
    };
    this.net.broadcast(encode(message));
  }

  snapshot(): SimState {
    return this.sim.snapshot();
  }
}

export class GuestSession {
  readonly buffer = new SnapshotBuffer();
  private readonly gate = new SequenceGate();
  private seq = 0;
  private lastPingAt = -Infinity;

  /** Smoothed round-trip time in ms, or null before the first pong. */
  rttMs: number | null = null;
  /** Set by the owner to reconcile prediction against each fresh snapshot. */
  onSnapshot: ((state: SimState) => void) | null = null;

  constructor(readonly net: NetGuest) {}

  /** Returns true if the message belonged to the match rather than the lobby. */
  handleMessage(message: NetMessage, now: number): boolean {
    switch (message.t) {
      case 'snap': {
        if (this.gate.accept(message.seq)) {
          this.buffer.push(message.state, now);
          this.onSnapshot?.(message.state);
        }
        return true;
      }
      case 'pong': {
        const rtt = now - message.sent;
        this.rttMs = this.rttMs === null ? rtt : this.rttMs * 0.8 + rtt * 0.2;
        return true;
      }
      default:
        return false;
    }
  }

  /**
   * Send one input. The caller paces this on the sim clock -- every second tick
   * of a 120 Hz loop is the 60 Hz the spec asks for -- and tags it with the tick
   * it was predicted on, which is what makes reconciliation exact.
   */
  sendInput(tick: number, input: BlobInput): void {
    if (!this.net.isOpen) return;
    this.net.send(encode({ t: 'input', seq: this.seq++, tick, input }));
  }

  /** Round-trip probe, once a second. */
  pingIfDue(now: number): void {
    if (!this.net.isOpen) return;
    if (now - this.lastPingAt < PING_INTERVAL_MS) return;
    this.lastPingAt = now;
    this.net.send(encode({ t: 'ping', sent: now }));
  }

  /** Interpolated view for the renderer, or null before the first snapshot. */
  view(now: number): SnapshotView | null {
    return this.buffer.sample(now);
  }
}
